import { Link, usePage } from "@inertiajs/react"
import { useState } from "react"
import { Logo } from "./Logo"
import { Button } from "./Button"
import { cn } from "@/lib/cn"

type NavItem = { label: string; href: string }

const primaryNav: NavItem[] = [
  { label: "Features", href: "/features" },
  { label: "How it works", href: "/how-it-works" },
  { label: "Pricing", href: "/pricing" },
  { label: "Developers", href: "/developers" },
  { label: "Use cases", href: "/use-cases" },
  { label: "Blog", href: "/blog" },
  { label: "Help", href: "/help" },
]

const compareNav: NavItem[] = [
  { label: "vs. NeverBounce", href: "/compare/neverbounce" },
  { label: "vs. ZeroBounce", href: "/compare/zerobounce" },
  { label: "vs. Findymail", href: "/compare/findymail" },
]

function ChevronDown({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 16 16" fill="none" className={cn("size-4 shrink-0", className)} aria-hidden="true">
      <path d="M4 6 L8 10 L12 6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  )
}

function MenuIcon({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 16 16" fill="none" className={cn("size-5 shrink-0", className)} aria-hidden="true">
      <path d="M3 5h10M3 8h10M3 11h10" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
    </svg>
  )
}

function CloseIcon({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 16 16" fill="none" className={cn("size-5 shrink-0", className)} aria-hidden="true">
      <path d="M4 4l8 8M12 4l-8 8" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
    </svg>
  )
}

export function Header() {
  const { pathname = "/" } = usePage().props as unknown as { pathname?: string }
  const [mobileOpen, setMobileOpen] = useState(false)
  const [compareOpen, setCompareOpen] = useState(false)

  return (
    <header className="sticky top-0 z-40 border-b border-ink-950/5 bg-white/85 backdrop-blur-md">
      <div className="mx-auto flex h-16 w-full max-w-7xl items-center justify-between px-6 lg:px-8">
        <Link href="/" aria-label="Homepage" className="relative -my-2 inline-flex items-center">
          <Logo />
        </Link>

        <nav className="hidden items-center gap-x-1 lg:flex" aria-label="Primary">
          {primaryNav.map((item) => {
            const active = pathname === item.href
            return (
              <Link
                key={item.href}
                href={item.href}
                className={cn(
                  "rounded-lg px-3 py-2 text-sm font-medium tracking-tight transition",
                  active ? "text-ink-950 bg-ink-100" : "text-ink-700 hover:text-ink-950 hover:bg-ink-50",
                )}
              >
                {item.label}
              </Link>
            )
          })}

          <div
            className="relative"
            onMouseEnter={() => setCompareOpen(true)}
            onMouseLeave={() => setCompareOpen(false)}
          >
            <button
              type="button"
              onClick={() => setCompareOpen((v) => !v)}
              aria-expanded={compareOpen}
              aria-haspopup="menu"
              className={cn(
                "inline-flex items-center gap-x-1 rounded-lg px-3 py-2 text-sm font-medium tracking-tight transition",
                pathname.startsWith("/compare")
                  ? "text-ink-950 bg-ink-100"
                  : "text-ink-700 hover:text-ink-950 hover:bg-ink-50",
              )}
            >
              Compare
              <ChevronDown className={cn("transition", compareOpen && "rotate-180")} />
            </button>
            <div
              role="menu"
              className={cn(
                "absolute top-full left-0 z-50 w-56 pt-2 transition",
                compareOpen ? "opacity-100 translate-y-0" : "pointer-events-none translate-y-1 opacity-0",
              )}
            >
              <div className="overflow-hidden rounded-2xl bg-white p-1.5 ring-1 ring-ink-950/8 shadow-lg shadow-ink-950/8">
                {compareNav.map((item) => (
                  <Link
                    key={item.href}
                    href={item.href}
                    role="menuitem"
                    className="block rounded-lg px-3 py-2 text-sm font-medium text-ink-700 hover:bg-ink-50 hover:text-ink-950"
                  >
                    {item.label}
                  </Link>
                ))}
              </div>
            </div>
          </div>
        </nav>

        <div className="hidden items-center gap-x-2 lg:flex">
          <Button as="a" href="/sign-in" variant="ghost" size="sm">
            Sign in
          </Button>
          <Button as="a" href="/sign-up" variant="primary" size="sm">
            Start free
          </Button>
        </div>

        <button
          type="button"
          onClick={() => setMobileOpen(true)}
          className="relative inline-flex size-10 items-center justify-center rounded-lg text-ink-700 hover:bg-ink-50 lg:hidden"
          aria-label="Open menu"
        >
          <MenuIcon />
          <span aria-hidden="true" className="absolute top-1/2 left-1/2 size-[max(100%,3rem)] -translate-1/2 pointer-fine:hidden" />
        </button>
      </div>

      {mobileOpen && (
        <div className="fixed inset-0 z-50 lg:hidden" role="dialog" aria-modal="true">
          <div className="absolute inset-0 bg-ink-950/30" onClick={() => setMobileOpen(false)} aria-hidden="true" />
          <div className="absolute top-0 right-0 left-0 isolate flex flex-col gap-y-4 bg-white px-6 pt-4 pb-6 ring-1 ring-ink-950/5">
            <div className="flex items-center justify-between">
              <Link href="/" aria-label="Homepage" onClick={() => setMobileOpen(false)}>
                <Logo />
              </Link>
              <button
                type="button"
                onClick={() => setMobileOpen(false)}
                className="relative inline-flex size-10 items-center justify-center rounded-lg text-ink-700 hover:bg-ink-50"
                aria-label="Close menu"
              >
                <CloseIcon />
                <span aria-hidden="true" className="absolute top-1/2 left-1/2 size-[max(100%,3rem)] -translate-1/2 pointer-fine:hidden" />
              </button>
            </div>
            <nav className="flex flex-col gap-y-0.5" aria-label="Mobile primary">
              {primaryNav.map((item) => (
                <Link
                  key={item.href}
                  href={item.href}
                  onClick={() => setMobileOpen(false)}
                  className="rounded-lg px-3 py-2.5 text-base font-medium tracking-tight text-ink-800 hover:bg-ink-50"
                >
                  {item.label}
                </Link>
              ))}
              <div className="mt-2 border-t border-ink-950/5 pt-2">
                <p className="px-3 py-1 text-xs font-medium tracking-wide text-ink-500 uppercase">Compare</p>
                {compareNav.map((item) => (
                  <Link
                    key={item.href}
                    href={item.href}
                    onClick={() => setMobileOpen(false)}
                    className="block rounded-lg px-3 py-2 text-base font-medium tracking-tight text-ink-700 hover:bg-ink-50"
                  >
                    {item.label}
                  </Link>
                ))}
              </div>
            </nav>
            <div className="flex flex-col gap-y-2 border-t border-ink-950/5 pt-4">
              <Button as="a" href="/sign-in" variant="secondary" size="md" className="w-full">
                Sign in
              </Button>
              <Button as="a" href="/sign-up" variant="primary" size="md" className="w-full">
                Start free
              </Button>
            </div>
          </div>
        </div>
      )}
    </header>
  )
}
